import { ref, watchEffect } from 'vue'
const { warn } = useLogger()

// Define allowed Open Graph types for better type safety
type OgType =
  | 'website'
  | 'article'
  | 'book'
  | 'profile'
  | 'music.song'
  | 'music.album'
  | 'music.playlist'
  | 'music.radio_status'
  | 'video.movie'
  | 'video.episode'
  | 'video.tv_show'
  | 'video.other'

// Define types for dynamic OG images - Reverting to original
export type OgImageType = 'site_default' | 'share_collection' | 'share_bubble' | 'share_set'

interface PageMetaOptions {
  title?: string
  description?: string
  ogImageType?: OgImageType
  ogImageShareUuid?: string // For 'share_collection', 'share_bubble'
  ogImageTargetBubbleType?: string // For 'share_set'
  type?: OgType
  url?: string
}

export const usePageTitle = (options: PageMetaOptions = {}) => {
  const {
    title = 'grep - Your Digital Sidekick',
    description = 'grep: Your digital sidekick for everything you want to remember and discover',
    ogImageType = 'site_default',
    ogImageShareUuid,
    ogImageTargetBubbleType,
    type = 'website',
    url,
  } = options

  const config = useRuntimeConfig()
  const route = useRoute()
  const defaultUrl = new URL(route.fullPath, config.public.siteUrl).href

  const titleData = ref({
    title,
    description,
    ogImageType,
    ogImageShareUuid,
    ogImageTargetBubbleType,
    type,
    url: url || defaultUrl,
  })

  const getComputedTitle = (titleChunk?: string | null) => {
    const baseTitle = 'grep - Your Digital Sidekick'
    if (!titleChunk || titleChunk === baseTitle) {
      return baseTitle
    }
    return `${titleChunk} - grep`
  }

  watchEffect(() => {
    const computedTitle = getComputedTitle(titleData.value.title)
    let ogImageFinalUrl = ''
    const r2PublicBaseUrl = 'https://data.grepit.app' // Base URL for R2 assets

    switch (titleData.value.ogImageType) {
      case 'share_collection':
      case 'share_bubble':
      case 'share_set':
        if (titleData.value.ogImageShareUuid) {
          ogImageFinalUrl = `${r2PublicBaseUrl}/og_images/share/${titleData.value.ogImageShareUuid}.png`
        } else {
          warn(
            `OG Image: share_uuid missing for type ${titleData.value.ogImageType}, falling back to default png.`
          )
          ogImageFinalUrl = `${r2PublicBaseUrl}/assets/grepit-logo-og.png` // Default remains .png
        }
        break
      case 'site_default':
      default:
        ogImageFinalUrl = `${r2PublicBaseUrl}/assets/grepit-logo-og.png` // Default remains .png
        break
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const seoMetaOptions: any = {
      title: titleData.value.title,
      titleTemplate: getComputedTitle,
      description: titleData.value.description,
      ogTitle: computedTitle,
      ogDescription: titleData.value.description,
      ogType: titleData.value.type,
      ogUrl: titleData.value.url,
      twitterTitle: computedTitle,
      twitterDescription: titleData.value.description,
      twitterCard: 'summary_large_image',
      mobileWebAppCapable: 'yes',
      appleMobileWebAppCapable: 'yes',
      appleMobileWebAppTitle: 'grep',
      appleMobileWebAppStatusBarStyle: 'black-translucent',
      formatDetection: 'telephone=no',
      themeColor: [
        { media: '(prefers-color-scheme: light)', content: '#ffffff' },
        { media: '(prefers-color-scheme: dark)', content: '#000000' },
      ],
      htmlAttrs: {
        lang: 'en',
      },
    }

    if (ogImageFinalUrl) {
      seoMetaOptions.ogImage = [{ url: ogImageFinalUrl, width: 1200, height: 630 }]
      seoMetaOptions.twitterImage = ogImageFinalUrl
    }

    // Add favicon link
    seoMetaOptions.link = [{ rel: 'icon', type: 'image/svg+xml', href: '/images/new_back.svg' }]

    useSeoMeta(seoMetaOptions)
  })

  return {
    updateTitle: (newTitle: string) => {
      titleData.value.title = newTitle
    },
    updateDescription: (newDescription: string) => {
      titleData.value.description = newDescription
    },
    updateUrl: (newUrl: string) => {
      titleData.value.url = newUrl
    },
    updateOgImageDetails: (details: {
      ogImageType?: OgImageType
      ogImageShareUuid?: string
      ogImageTargetBubbleType?: string
    }) => {
      if (details.ogImageType !== undefined) titleData.value.ogImageType = details.ogImageType
      if (details.ogImageShareUuid !== undefined)
        titleData.value.ogImageShareUuid = details.ogImageShareUuid
      if (details.ogImageTargetBubbleType !== undefined)
        titleData.value.ogImageTargetBubbleType = details.ogImageTargetBubbleType
    },
  }
}
